import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Consts } from '@ortho-next/nextray-core';
import { BaseComponent } from '../base';


/**
 * Base component for mechanical axis table data. 
 */
@Component({
	selector: 'base-ma-table',
	templateUrl: './base-ma-table.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
})
export class BaseMaTableComponent extends BaseComponent {

	protected readonly apAngles = { ...Consts.paleyAngles.AP };
	protected readonly ltAngles = { ...Consts.paleyAngles.LT };

	// VISIBILITY

	/**
	* Check if MAD is visible.
	*/
	get isMAD(): boolean {
		return this.isLongLeg;
	}
	/**
	* Check if MNSA is visible.
	*/
	get isMNSA(): boolean {
		return this.isLongLeg || this.isFemur;
	}
	/**
	* Check if LPFA is visible.
	*/
	get isLPFA(): boolean {
		return this.isLongLeg || this.isFemur;
	}
	/**
	* Check if MPFA is visible.
	*/
	get isMPFA(): boolean {
		return this.isLongLeg || this.isFemur;
	}
	/**
	* Check if MLDFA is visible.
	*/
	get isMLDFA(): boolean {
		return this.isLongLeg || this.isFemur;
	}
	/**
	* Check if JLCA is visible.
	*/
	get isJLCA(): boolean {
		return this.isLongLeg;
	}
	/**
	* Check if Knee Translation is visible.
	*/
	get isKneeTransl(): boolean {
		return this.isLongLeg;
	}
	/**
	* Check if MPTA is visible.
	*/
	get isMPTA(): boolean {
		return this.isLongLeg || this.isTibia;
	}
	/**
	* Check if LDTA is visible.
	*/
	get isLDTA(): boolean {
		return this.isLongLeg || this.isTibia;
	}
	/**
	* Check if PDFA is visible.
	*/
	get isPDFA(): boolean {
		return this.isLongLeg || this.isFemur;
	}
	/**
	* Check if PPTA is visible.
	*/
	get isPPTA(): boolean {
		return this.isLongLeg || this.isTibia;
	}
	/**
	* Check if ADTA is visible.
	*/
	get isADTA(): boolean {
		return this.isLongLeg || this.isTibia;
	}

	// ANGLES RANGE

	/**
	* Get MAD range.
	*/
	get rangeMAD(): string {
		return `(${this.apAngles.MAD.min}mm - ${this.apAngles.MAD.max}mm)`;
	}
	/**
	* Get MNSA range.
	*/
	get rangeMNSA(): string {
		return `(${this.apAngles.MNSA.min}° - ${this.apAngles.MNSA.max}°)`;
	}
	/**
	* Get LPFA range.
	*/
	get rangeLPFA(): string {
		return `(${this.apAngles.mLPFA.min}° - ${this.apAngles.mLPFA.max}°)`;
	}
	/**
	* Get MPFA range.
	*/
	get rangeMPFA(): string {
		return `(${this.apAngles.MPFA.min}° - ${this.apAngles.MPFA.max}°)`;
	}
	/**
	* Get MLDFA range.
	*/
	get rangeMLDFA(): string {
		return `(${this.apAngles.mLDFA.min}° - ${this.apAngles.mLDFA.max}°)`;
	}
	/**
	* Get JLCA range.
	*/
	get rangeJLCA(): string {
		return `(${this.apAngles.JLCA.min}° - ${this.apAngles.JLCA.max}°)`;
	}
	/**
	* Get Knee Translation range.
	*/
	get rangeKneeTransl(): string {
		return `(${this.apAngles.KneeTransl.min}mm - ${this.apAngles.KneeTransl.max}mm)`;
	}
	/**
	* Get MPTA range.
	*/
	get rangeMPTA(): string {
		return `(${this.apAngles.MPTA.min}° - ${this.apAngles.MPTA.max}°)`;
	}
	/**
	* Get LDTA range.
	*/
	get rangeLDTA(): string {
		return `(${this.apAngles.LDTA.min}° - ${this.apAngles.LDTA.max}°)`;
	}

	/**
	* Get PDFA range.
	*/
	get rangePDFA(): string {
		return `(${this.ltAngles.PDFA.min}° - ${this.ltAngles.PDFA.max}°)`;
	}
	/**
	* Get PPTA range.
	*/
	get rangePPTA(): string {
		return `(${this.ltAngles.PPTA.min}° - ${this.ltAngles.PPTA.max}°)`;
	}
	/**
	* Get ADTA range.
	*/
	get rangeADTA(): string {
		return `(${this.ltAngles.ADTA.min}° - ${this.ltAngles.ADTA.max}°)`;
	}

	// LLD DATA

	/**
	* Get Limb Lenght Discrepancy measure of long leg.
	*/
	get longlegLLD(): string {
		return this.calculateLLD(this.defAPMeasures.def_ap_fullLength, this.defCLMeasures.def_ap_fullLength_ctrlateral);
	}
	/**
	* Get Limb Lenght Discrepancy measure of femur.
	*/
	get femurLLD(): string {
		return this.calculateLLD(this.defAPMeasures.def_ap_femurLength, this.defCLMeasures.def_ap_femurLength_ctrlateral);
	}
	/**
	* Get Limb Lenght Discrepancy measure of tibia.
	*/
	get tibiaLLD(): string {
		return this.calculateLLD(this.defAPMeasures.def_ap_tibiaLength, this.defCLMeasures.def_ap_tibiaLength_ctrlateral);
	}
	/**
	* Get True Lenght measure.
	*/
	get trueLength(): number | undefined {
		return this.defAPMeasures.def_ap_femurLength && this.defAPMeasures.def_ap_tibiaLength ? this.defAPMeasures.def_ap_femurLength + this.defAPMeasures.def_ap_tibiaLength : undefined;
	}
	/**
	* Get True Lenght measure of Contralateral.
	*/
	get trueLengthCL(): number | undefined {
		return this.defCLMeasures.def_ap_femurLength_ctrlateral && this.defCLMeasures.def_ap_tibiaLength_ctrlateral ? this.defCLMeasures.def_ap_femurLength_ctrlateral + this.defCLMeasures.def_ap_tibiaLength_ctrlateral : undefined;
	}
	/**
	* Get True Lenght measure of Limb Lenght Discrepancy.
	*/
	get trueLengthLLD(): string {
		return this.calculateLLD(this.trueLength, this.trueLengthCL);
	}
	/**
	* Get Limb Lenght Discrepancy measure of long leg.
	*/
	get rpmLonglegLLD(): string {
		return this.calculateLLD(this.rpmMeasures?.rpm_ap_fullLength, this.defCLMeasures.def_ap_fullLength_ctrlateral);
	}
	/**
	* Get Limb Lenght Discrepancy measure of femur.
	*/
	get rpmFemurLLD(): string {
		return this.calculateLLD(this.rpmMeasures?.rpm_ap_femurLength, this.defCLMeasures.def_ap_femurLength_ctrlateral);
	}
	/**
	* Get Limb Lenght Discrepancy measure of tibia.
	*/
	get rpmTibiaLLD(): string {
		return this.calculateLLD(this.rpmMeasures?.rpm_ap_tibiaLength, this.defCLMeasures.def_ap_tibiaLength_ctrlateral);
	}
	/**
	* Get True Lenght measure.
	*/
	get rpmTrueLength(): number | undefined {
		return this.rpmMeasures?.rpm_ap_femurLength && this.rpmMeasures.rpm_ap_tibiaLength ? this.rpmMeasures.rpm_ap_femurLength + this.rpmMeasures.rpm_ap_tibiaLength : undefined;
	}
	/**
	* Get True Lenght measure of Limb Lenght Discrepancy.
	*/
	get rpmTrueLengthLLD(): string {
		return this.calculateLLD(this.rpmTrueLength, this.rpmTrueLengthCL);
	}
	/**
	* Get True Lenght measure of Contralateral.
	*/
	get rpmTrueLengthCL(): number | undefined {
		return this.defCLMeasures.def_ap_femurLength_ctrlateral && this.defCLMeasures.def_ap_tibiaLength_ctrlateral ? this.defCLMeasures.def_ap_femurLength_ctrlateral + this.defCLMeasures.def_ap_tibiaLength_ctrlateral : undefined;
	}

	private calculateLLD(length?: number, cl_length?: number): string {
		if (length && cl_length) {
			const lld = length - cl_length;
			let sign = null;
			if (lld > 0) sign = this.labels().LLD_TABLE_LONG;
			if (lld < 0) sign = this.labels().LLD_TABLE_SHORT;
			return sign ? `${Math.abs(lld)} ${sign}` : `${lld}`;
		}
		return '';
	}

}
